// UsersConfig.tsx
import React, { useState, useEffect } from 'react';
import { useUserStore } from '@/store/userStore';
import { AddUserModal } from '@/components/modals/AddUserModal';
import { Edit2, Trash2 } from 'lucide-react';
import { USER_ROLES, USER_STATUS } from '@/utils/constants';

export const UsersConfig: React.FC<{ onSetActionCallback?: (callbacks: { add?: () => void }) => void }> = ({ onSetActionCallback }) => {
  const { users, loading, fetchUsers, deleteUser } = useUserStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    onSetActionCallback?.({
      add: () => setShowAddModal(true)
    });
  }, [onSetActionCallback]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedUsers(users.map(user => user._id || user.id || ''));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      await deleteUser(id);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case USER_STATUS.ACTIVE:
        return 'bg-green-100 text-green-800';
      case USER_STATUS.INVITED:
        return 'bg-yellow-100 text-yellow-800';
      case USER_STATUS.INACTIVE:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getUserInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getUserColor = (index: number) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-red-500', 'bg-yellow-500'];
    return colors[index % colors.length];
  };

  return (
    <div className="h-full bg-white mx-0 my-0">
      <div className="h-full flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="w-full border border-gray-200 border-collapse">
            <thead>
              <tr className="border border-gray-200">
                <th className="text-left bg-gray-100 px-4 border py-1 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300"
                    onChange={handleSelectAll}
                    checked={selectedUsers.length === users.length && users.length > 0}
                  />
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  USER
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  EMAIL ADDRESS
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  ROLE
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  ADDED ON
                </th>
                <th className="w-20 bg-gray-100 border"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500 border">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500 border">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user._id || user.id} className="border border-gray-100 hover:bg-gray-50">
                    <td className="px-4 border py-1">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300"
                        checked={selectedUsers.includes(user._id || user.id || '')}
                        onChange={() => handleSelectUser(user._id || user.id || '')}
                      />
                    </td>
                    <td className="px-4 border py-1">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${getUserColor(index)}`}>
                          {getUserInitials(user.name)}
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-900">{user.name}</span>
                      </div>
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">{user.email}</td>
                    <td className="px-4 border py-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        user.role === USER_ROLES.ADMIN ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 border py-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status)}`}>
                        {user.status || USER_STATUS.ACTIVE}
                      </span>
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 border py-1">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          onClick={() => handleDelete(user._id || user.id || '', user.name)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 border py-3 border-t border-gray-200 bg-gray-50 flex items-center">
          <div className="text-sm text-gray-600 uppercase">
            COUNT <span className="ml-4 font-medium">{users.length}</span>
          </div>
        </div>
      </div>

      <AddUserModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
    </div>
  );
};